/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {
    Box,
    Container,
    Stack,

    // Hooks
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import LinksList from '../links-list'

// Others
import {categoryUrlBuilder} from '../../utils/url'

const ListMenuContent = ({item, itemsKey, onClose, initialFocusRef}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const {locale} = useIntl()
    const items = item?.[itemsKey] || []
    const filteredItems = items.filter(({c_showInMenu}) => c_showInMenu)
    const parentItem = item
    return (
        <Container as={Stack} {...baseStyle.popoverContainer} className="list-menu-container">
            <Box
                spacing={8}
                justifyContent={'left'}
                display="flex"
                marginInlineStart="0"
                className="list-menu-flex"
            >
                <Box
                    spacing={1}
                    className="nav-categories"
                    display={!items.every((i) => !i.categories) ? 'flex' : 'block'}
                    width="50%"
                    justifyContent="flex-start"
                    gap={{lg: '30px', xl: '60px'}}
                    alignItems="flex-start"
                    flex="1 0 0"
                >
                    {filteredItems.map((item, index) => {
                        const {id, name} = item
                        const items = item[itemsKey]

                        const heading = {
                            href: categoryUrlBuilder(item, locale),
                            text: name
                        }

                        const links = items
                            ? items.map((item) => {
                                  const {
                                      name,
                                      id,
                                      c_showInMenu,
                                      c_isExternalRedirection,
                                      c_badgeTextColor,
                                      c_badgeText
                                  } = item
                                  return {
                                      href: categoryUrlBuilder(item, locale),
                                      text: name,
                                      id: id,
                                      c_showInMenu: c_showInMenu,
                                      c_isExternalRedirection: c_isExternalRedirection,
                                      c_badgeText: c_badgeText,
                                      c_badgeTextColor: c_badgeTextColor
                                  }
                              })
                            : []
                        // Get matching markup from c_desktopNavAssets
                        const matchedAssets = parentItem?.c_desktopNavAssets?.filter((asset) => {
                            const match = asset.id.match(/^(\d+)_/)
                            return match && parseInt(match[1], 10) === index
                        })

                        const allSameIndex = parentItem?.c_desktopNavAssets
                            ?.map((a) => a?.id?.split('_')?.[0])
                            ?.every((v, _, arr) => v === arr?.[0])
                        return (
                            <LinksList
                                key={id}
                                matchedAssets={matchedAssets}
                                allSameIndex={allSameIndex}
                                totalColumns={filteredItems.length}
                                columnIndex={index}
                                heading={heading}
                                links={links}
                                item={item}
                                width="fit-content"
                                color={'gray.900'}
                                onLinkClick={onClose}
                                {...(index === 0 ? {headingLinkRef: initialFocusRef} : {})}
                            />
                        )
                    })}
                </Box>
            </Box>
        </Container>
    )
}

ListMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object
}

export {ListMenuContent}
